import DeleteIcon from "@mui/icons-material/Delete";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import * as ids from "common/ids";
import Component from "components/Component";
import Disabled from "components/common/Disabled";
import IconButton from "components/common/IconButton";
import Notes from "components/common/Notes";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import * as labels from "config.labels";
import React from "react";
import { connect } from "react-redux";
import * as claimsActions from "store/actions/input/claims/claimsActions";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import { selectSelfInsuredRetentions } from "store/selectors/input/claims/claimsSelectors";
import * as userSelectors from "store/selectors/user/userSelectors";

const SelfInsuredRetentions = (props) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const blankIdRef = React.useRef(null);
  const getUniqueBlankId = (retentions) => {
    const existingIds = new Set((retentions || []).map((r) => String(r?.id)));
    let candidate = blankIdRef.current ?? ids.makeNewId();
    while (existingIds.has(String(candidate))) {
      candidate = ids.makeNewId();
    }
    blankIdRef.current = candidate;
    return candidate;
  };

  const appendBlankAndAddDeleteFlag = (retentions) => {
    return retentions
      .map((retention) => ({ canDelete: true, ...retention }))
      .concat({
        id: getUniqueBlankId(retentions),
        lossType: null,
        value: null,
        notes: null,
        canDelete: false,
      });
  };
  const CoverageSelector = (props) => (
    <Disabled ifReadOnly>
      <Select
        variant={"standard"}
        onChange={props.onChange}
        value={props.value}
        fullWidth
      >
        <MenuItem hidden disabled sx={{ display: "none" }} value={null}>
          {translateLabel(labels.SELF_INSURED_RETENTIONS_NOT_SELECTED, {
            default: "Not Selected",
          })}
        </MenuItem>
        <MenuItem value={"ALL"}>
          {translateLabel(labels.SELF_INSURED_RETENTIONS_ALL, {
            default: "All",
          })}
        </MenuItem>
        {(props.coverages ?? {}).map((coverage) => (
          <MenuItem key={coverage.key} value={coverage.key}>
            {coverage.display}
          </MenuItem>
        ))}
        {props.includeOther && (
          <MenuItem value={"OTHER"}>
            {translateLabel(labels.SELF_INSURED_RETENTIONS_OTHER, {
              default: "Other",
            })}
          </MenuItem>
        )}
      </Select>
    </Disabled>
  );

  return (
    <Component
      title={translateLabel(labels.SELF_INSURED_RETENTIONS_TITLE, {
        default: "Self-Insured Retentions",
      })}
      {...props.componentProps}
    >
      <Table>
        <colgroup>
          <col style={{ width: "0px" }} />
          <col style={{ width: "150px" }} />
          <col style={{ width: "250px" }} />
          <col style={{}} />
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell></TableCell>
            <TableCell>
              {translateLabel(labels.SELF_INSURED_RETENTIONS_COVERAGE, {
                default: "Coverage",
              })}
            </TableCell>
            <TableCell align={"right"}>
              {translateLabel(labels.SELF_INSURED_RETENTIONS_AMOUNT, {
                default: "Amount",
              })}
            </TableCell>
            <TableCell>
              {translateLabel(labels.SELF_INSURED_RETENTIONS_NOTES, {
                default: "Notes",
              })}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {appendBlankAndAddDeleteFlag(props.selfInsuredRetentions).map(
            (retention, index) => (
              <TableRow key={retention.id ?? "blank"}>
                <TableCell>
                  {retention.canDelete ? (
                    <IconButton
                      icon={DeleteIcon}
                      onClick={() =>
                        props.deleteSelfInsuredRetention(retention.id)
                      }
                    />
                  ) : (
                    <></>
                  )}
                </TableCell>
                <TableCell>
                  <CoverageSelector
                    coverages={props.lossCategories}
                    value={retention.lossType || ""}
                    onChange={(e) =>
                      props.updateSelfInsuredRetention({
                        id: retention.id ?? ids.makeNewId(),
                        lossType: e.target.value,
                      })
                    }
                    includeOther={props.includeOther}
                  />
                </TableCell>
                <TableCell>
                  <PrettyNumberTextField
                    type={"text"}
                    inputProps={{ style: { textAlign: "right" } }}
                    hiddenLabel
                    size={"small"}
                    variant={"filled"}
                    onChangeNumber={(x) =>
                      props.updateSelfInsuredRetention({
                        id: retention.id ?? ids.makeNewId(),
                        value: x,
                      })
                    }
                    value={retention.value}
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <Notes
                    title={null}
                    update={(comment) =>
                      props.updateSelfInsuredRetention({
                        id: retention.id ?? ids.makeNewId(),
                        notes: comment,
                      })
                    }
                    value={retention.notes}
                    hasEditButton={false}
                  />
                </TableCell>
              </TableRow>
            )
          )}
        </TableBody>
      </Table>
    </Component>
  );
};

const mapDispatchToProps = {
  updateSelfInsuredRetention: claimsActions.updateSelfInsuredRetention,
  deleteSelfInsuredRetention: claimsActions.deleteSelfInsuredRetention,
};

const mapStateToProps = (state) => {
  return {
    lossCategories: state.temp.exposureMetrics.lossCategories || [],
    selfInsuredRetentions: selectSelfInsuredRetentions(state),
    includeOther:
      userSelectors.selectUserConfig(state)?.componentVisibility?.[
        "input.claims.selfInsuredRetentions.other"
      ] ?? true,
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SelfInsuredRetentions);
